import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { History } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { AUDIT } from '@/constants/testIds';

const FIELD_LABELS = {
  maara_kg: 'Määrä',
  status: 'Status',
  lahete: 'Lähete',
  ostotilaus: 'Ostotilaus',
  reitti: 'Reitti',
  kirjattu: 'Kirjattu',
};

function formatVal(v) {
  if (v == null || v === '') return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export default function AuditPopover({ date, customerId }) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/audit/orders/${date}/${customerId}`)
      .then(({ data }) => setEntries(data))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [open, date, customerId]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={`${AUDIT.openPrefix}${customerId}`}
          className="w-7 h-7 rounded-full hover:bg-[#F4F6F3] text-[#6A6D66] hover:text-[#1C1C1A] flex items-center justify-center transition-colors"
          aria-label="Historia"
        >
          <History size={13} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 max-h-80 overflow-y-auto p-0">
        <div className="p-3 border-b border-[#E5E6E3] sticky top-0 bg-white">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#6A6D66]">Historia</p>
          <p className="text-sm text-[#1C1C1A]">Tilauksen muutokset</p>
        </div>
        <ul className="divide-y divide-[#E5E6E3]" data-testid={`${AUDIT.listPrefix}${customerId}`}>
          {loading && <li className="p-3 text-xs text-[#6A6D66]">Ladataan…</li>}
          {!loading && entries.length === 0 && <li className="p-3 text-xs text-[#6A6D66]">Ei muutoksia.</li>}
          {entries.map((e) => {
            const ts = new Date(e.ts);
            return (
              <li key={e.id} className="p-3 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-[#1C1C1A]">{e.user_name || 'Tuntematon'}</span>
                  <span className="text-[#6A6D66]">{ts.toLocaleString('fi-FI', { dateStyle: 'short', timeStyle: 'short' })}</span>
                </div>
                <div className="mt-1 space-y-0.5">
                  {Object.entries(e.diff || {}).map(([field, dv]) => (
                    <div key={field} className="text-[#1C1C1A]">
                      <span className="text-[#6A6D66]">{FIELD_LABELS[field] || field}:</span>{' '}
                      <span className="line-through text-[#6A6D66]">{formatVal(dv.before)}</span>{' → '}
                      <span className="font-medium">{formatVal(dv.after)}</span>
                    </div>
                  ))}
                  {e.source === 'pdf' && (
                    <div className="text-[#3B6E44] text-[11px]">PDF: {e.filename}</div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
